/**
 * Transaction Coordinator service — resolves the TC's brokerage-linked agents
 * and firm profile.
 *
 * GET /api/v1/tc/agents (API_ENDPOINTS.tcAgents) — returns the calling TC's
 * brokerage agents as LinkedAgentResponse[]: { userId, firstName, lastName,
 * email, licenseState, profilePhotoUrl, ... }. Mirrors mobile's tc.service.ts.
 */
import { apiClient, API_ENDPOINTS } from '../config/api';

export const tcService = {
  getLinkedAgents: async (firebaseToken) => {
    const response = await apiClient.get(API_ENDPOINTS.tcAgents, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
    });
    const data = response.data;
    const list = Array.isArray(data) ? data : (data?.content || data?.data || []);
    // Normalize LinkedAgentResponse's `userId` to `id` — the Primary Agent picker
    // (Purchase/ListingTransactionPage) reads agent.id uniformly regardless of source.
    return list.map((a) => ({ ...a, id: a.id || a.userId }));
  },

  // GET /api/v1/tc/brokerage — the TC's affiliated brokerage, joined via the
  // licence #/state stored on their UserProfile at registration. Returns null
  // when the TC never verified a brokerage or the backend returns 204 (no
  // brokerage on file). When the firm exists but isn't registered on Rialty,
  // the response is a licence-only stub. Mirrors mobile's tcService.getMyBrokerage.
  getMyBrokerage: async (firebaseToken) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.tcBrokerage, {
        headers: { Authorization: `Bearer ${firebaseToken}` },
        validateStatus: (status) => status === 200 || status === 204,
      });
      if (response.status === 204 || !response.data) return null;
      const raw = response.data;
      return (raw?.data && typeof raw.data === 'object') ? raw.data : raw;
    } catch (error) {
      console.error('Error fetching TC brokerage:', error.response?.status, error.message);
      return null;
    }
  },
};
